import {
  APP_LABEL,
  PLATFORM_HOST,
  PLATFORM_SLUG,
  PRODUCTS,
} from '@app/shared/brand'
import {
  classify,
  fill,
  findActiveContent,
  type HtmlKind,
  hasBindings,
  sanitize,
} from '@segnavia/html'
import type { Format } from './format'
import {
  findHtmlElements,
  parseHtml,
  removeHtmlNode,
  serializeHtml,
} from './html-parser'

const wholeDocument =
  /^\s*<!doctype\s+html[^>]*>\s*<html(?:\s[^>]*)?>[\s\S]*<\/html>\s*$/i

/** Stored HTML pages contain no form controls. */
const FORM_CONTROL_ELEMENTS: ReadonlySet<string> = new Set([
  'form',
  'input',
  'button',
  'select',
  'textarea',
])

const isFormControl = (element: { name: string }) =>
  FORM_CONTROL_ELEMENTS.has(element.name.toLowerCase())

export type HtmlPageKind = HtmlKind

const productionOrigins = new Set([
  `https://${PLATFORM_HOST}`,
  ...Object.values(PRODUCTS).flatMap((product) => [
    `https://${product.host}`,
    `https://${APP_LABEL}.${product.host}`,
  ]),
])

function htmlPageLinkOrigins(extra?: Iterable<string>): Set<string> {
  const origins = new Set(productionOrigins)
  for (const value of extra ?? []) {
    try {
      const url = new URL(value)
      if (url.protocol === 'https:') origins.add(url.origin.toLowerCase())
    } catch {}
  }
  return origins
}

/** Page authoring embeds non-platform fonts as data: faces. */
const pageSanitize = { dataFonts: true } as const
const pageConverterPolicy = {
  urls: {
    linkProtocols: ['https:', 'mailto:'],
    imageProtocols: [],
    linkFragments: true,
    dataImages: true,
  },
  inertControls: [],
} as const

const pageSanitizePolicy = {
  ...pageSanitize,
  converterPolicy: pageConverterPolicy,
} as const

/**
 * A stylesheet construct Segnavia's sanitizer removes: `<style> …` or
 * `tag[style] …`, as `findActiveContent` reports them. Those never make a
 * page scripted.
 */
function isStylesheetConstruct(report: string): boolean {
  return report.startsWith('<style> ') || /^[^[\s]+\[style\] /.test(report)
}

/**
 * Segnavia's link policy reads `href` and `xlink:href` on every element, but
 * only an anchor is a link a reader follows. On an SVG `<image>`, `<use>` or
 * `<feImage>` the same attribute is a fetch the page makes on its own, so it
 * keeps the own-origin rule.
 */
function isFetchingReference(report: string): boolean {
  const match = /^([^[\s]+)\[(?:xlink:)?href\]/.exec(report)
  return Boolean(match && !LINK_ELEMENTS.has(match[1]?.toLowerCase() ?? ''))
}

const LINK_ELEMENTS = new Set(['a', 'area'])

function activeContent(
  body: string,
  options: { linkOrigins: Iterable<string>; keepScripts?: boolean },
): string[] {
  const strict = findActiveContent(body, { ...options, ...pageSanitize })
  const found = hasBindings(body, PLATFORM_SLUG)
    ? strict
    : [
        ...findActiveContent(body, { ...options, ...pageSanitizePolicy }),
        ...strict.filter(isFetchingReference),
      ]
  return [...new Set(found)].filter((item) => !isStylesheetConstruct(item))
}

/** Fill page bindings with Adanim's names and allowed link origins. */
export function fillHtmlPageData(input: {
  body: string
  data: Record<string, unknown>
  ownHttpsOrigins?: Iterable<string>
  unavailable?: Iterable<string>
}) {
  return fill(input.body, {
    prefix: PLATFORM_SLUG,
    data: input.data,
    linkOrigins: htmlPageLinkOrigins(input.ownHttpsOrigins),
    unavailable: input.unavailable,
    ...pageSanitize,
  })
}

function sanitizePage(
  body: string,
  options: { linkOrigins: Iterable<string>; keepScripts?: boolean },
): string {
  const policy = hasBindings(body, PLATFORM_SLUG)
    ? pageSanitize
    : pageSanitizePolicy
  return sanitize(body, { ...options, ...policy })
}

/** The active-content rule shared by every stored HTML page and slide. */
export function htmlIsScriptFree(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): boolean {
  return (
    activeContent(body, {
      linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
    }).length === 0
  )
}

/** A page's execution model follows only from the content it stores. */
export function htmlPageKind(body: string): HtmlPageKind {
  return classify(body, PLATFORM_SLUG)
}

/** The elements and attributes a stored HTML page refuses. */
export function htmlPageRefusedConstructs(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): string[] {
  const controls = findHtmlElements(
    parseHtml(body).children,
    isFormControl,
  ).map((element) => `<${element.name.toLowerCase()}>`)
  return [
    ...new Set([
      ...activeContent(body, {
        linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
        keepScripts: htmlPageKind(body) === 'scripted',
      }),
      ...controls,
    ]),
  ]
}

/** Legacy reads stay inert even though their old body can no longer be saved. */
export function stripHtmlScripts(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): string {
  return sanitizePage(body, {
    linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
  })
}

/** Remove disallowed active content while retaining a scripted page's scripts. */
export function sanitizeHtmlPage(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): string {
  const sanitized = sanitizePage(body, {
    linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
    keepScripts: htmlPageKind(body) === 'scripted',
  })
  const document = parseHtml(sanitized)
  const controls = findHtmlElements(document.children, isFormControl)
  if (!controls.length) return sanitized
  for (const element of controls) removeHtmlNode(element)
  return serializeHtml(document)
}

export function htmlIsValid(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): boolean {
  return (
    wholeDocument.test(body) &&
    htmlPageKind(body) !== 'refused' &&
    htmlPageRefusedConstructs(body, extraOwnOrigins).length === 0
  )
}

/** Remove active and embedded content before deriving searchable words. */
export function htmlSearchText(body: string): string {
  return body
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
    .replace(/data:[^\s"'<>)]*/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:nbsp|ensp|emsp);/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(?:39|x27);/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export const html = {
  bodyStorage: 'inline',
  validate: (body: string) => htmlIsValid(body),
  outline: () => [],
  searchText: htmlSearchText,
  tools: [],
} as const satisfies Format
